import { useEffect, useRef, useState } from "react";
import PropTypes from 'prop-types';
import Magnet from './Magnet';
import FloatingCards from './FloatingCards';
import "./graph-bg.css";
import './company-intro.css';

function HackerTypeOverlay({ text }) {
  const [render, setRender] = useState('');

  useEffect(() => {
    const source = text || '';
    const target = source.split('');
    const out = new Array(target.length).fill('');
    const symbols = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*_-+=';
    let i = 0;
    let ticks = 0;
    const maxTicks = 6; // scramble frames per character
    const interval = setInterval(() => {
      if (i >= target.length) {
        // hold for a short moment, then reset to loop
        setTimeout(() => {
          i = 0;
          ticks = 0;
          for (let k = 0; k < out.length; k++) out[k] = '';
          setRender('');
        }, 600);
        return;
      }
      if (ticks < maxTicks) {
        // show a random symbol at current position
        out[i] = symbols[Math.floor(Math.random() * symbols.length)];
        ticks++;
      } else {
        // lock the real character and move to next
        out[i] = target[i];
        i++;
        ticks = 0;
      }
      setRender(out.join(''));
    }, 50);

    return () => clearInterval(interval);
  }, [text]);

  return (
    <div className="terminal-overlay">
      <span className="hack-line">{render}</span>
      <span className="cursor" />
    </div>
  );
}

HackerTypeOverlay.propTypes = {
  text: PropTypes.string.isRequired,
};

const CompanyIntro = () => {
  const containerRef = useRef(null);
  const vectorRef = useRef(null);
  const [scrollOpacity, setScrollOpacity] = useState(1);
  // Fade out scroll down UI on scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY || window.pageYOffset;
      // Fade out between 0 and 120px scroll
      const opacity = Math.max(0, 1 - scrollY / 120);
      setScrollOpacity(opacity);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Removed GSAP animations. Floating and enter effects are handled via CSS in company-intro.css
  useEffect(() => {
    // no-op placeholder to keep effect order consistent
  }, []);

  return (
    <section
      ref={containerRef}
      className="relative min-h-[135vh] overflow-hidden graph-bg full-bleed"
      style={{ height: 'max(100vh, 700px)' }}
    >
      {/* Floating cards background */}
      <div className="absolute inset-0 z-0" style={{ height: '100%' }}>
        <FloatingCards />
      </div>

      {/* Floating Screen - Positioned at Left Bottom */}
      <div
        ref={vectorRef}
        className="absolute z-20 bottom-6 right-6 md:bottom-0 md:right-8 lg:bottom-0 lg:translate-y-[-55%]"
      >
    <Magnet padding={60} disabled={false} magnetStrength={20}>
      <div className="relative scale-[0.65] md:scale-[0.8] lg:scale-[0.9] vector-enter float-anim">
                {/* SVG Floating Screen */}
                <svg
                  width="400"
                  height="300"
                  viewBox="0 0 400 300"
                  className="drop-shadow-2xl"
                >
                {/* Laptop/Screen */}
                <rect
                  x="50"
                  y="80"
                  width="300"
                  height="180"
                  rx="15"
                  fill="url(#laptopGradient)"
                  stroke="#3B82F6"
                  strokeWidth="2"
                />
                <rect
                  x="60"
                  y="95"
                  width="280"
                  height="150"
                  rx="8"
                  fill="#1E293B"
                />
                
                {/* Hands typing */}
                <ellipse cx="120" cy="280" rx="25" ry="15" fill="#FBBF24" opacity="0.9"/>
                <ellipse cx="280" cy="280" rx="25" ry="15" fill="#FBBF24" opacity="0.9"/>
                
                {/* Floating feedback icons */}
                <circle cx="350" cy="50" r="15" fill="#10B981" opacity="0.8">
                  <animate attributeName="cy" values="50;40;50" dur="2s" repeatCount="indefinite"/>
                </circle>
                <text x="350" y="56" textAnchor="middle" fill="white" fontSize="12">✓</text>
                
                <circle cx="30" cy="120" r="12" fill="#F59E0B" opacity="0.7">
                  <animate attributeName="cy" values="120;110;120" dur="2.5s" repeatCount="indefinite"/>
                </circle>
                <text x="30" y="125" textAnchor="middle" fill="white" fontSize="10">★</text>
                
                {/* Gradients */}
                <defs>
                  <linearGradient id="laptopGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#475569"/>
                    <stop offset="100%" stopColor="#334155"/>
                  </linearGradient>
                </defs>
              </svg>
              
              {/* Hacking typing overlay positioned over the screen */}
              <HackerTypeOverlay text="Testimonial" />

              {/* Inline styles scoped to this component for the overlay */}
              <style>{`
                .terminal-overlay {
                  position: absolute;
                  left: 60px;
                  top: 95px;
                  width: 280px;
                  height: 150px;
                  pointer-events: none;
                  color: #22c55e;
                  display: flex;
                  align-items: flex-start;
                  justify-content: flex-start;
                  padding: 12px 10px;
                  box-sizing: border-box;
                  overflow: hidden;
                  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
                  text-shadow: 0 0 6px rgba(34, 197, 94, 0.65), 0 0 12px rgba(34, 197, 94, 0.35);
                  border: 1px solid rgba(34, 197, 94, 0.25);
                  box-shadow: inset 0 0 24px rgba(34, 197, 94, 0.18), 0 0 16px rgba(34, 197, 94, 0.16);
                  animation: crtFlicker 2.8s steps(60, end) infinite;
                  background: rgba(6, 12, 6, 0.25);
                }
                .terminal-overlay::after {
                  content: "";
                  position: absolute;
                  inset: 0;
                  pointer-events: none;
                  background: repeating-linear-gradient(
                    to bottom,
                    rgba(34, 197, 94, 0.08) 0px,
                    rgba(34, 197, 94, 0.08) 2px,
                    rgba(0, 0, 0, 0.0) 3px,
                    rgba(0, 0, 0, 0.0) 5px
                  );
                  opacity: 0.55;
                  animation: scan 7s linear infinite;
                }
                .terminal-overlay .hack-line {
                  white-space: nowrap;
                  font-size: 25px;
                  letter-spacing: 0.5px;
                }
                .terminal-overlay .cursor {
                  display: inline-block;
                  width: 10px;
                  height: 1.1em;
                  margin-left: 2px;
                  background: #22c55e;
                  box-shadow: 0 0 8px rgba(34, 197, 94, 0.9);
                  animation: blink 0.8s steps(1, end) infinite;
                  vertical-align: -2px;
                }
                @keyframes blink {
                  0%, 49% { opacity: 1; }
                  50%, 100% { opacity: 0; }
                }
                @keyframes scan {
                  0% { transform: translateY(0); }
                  100% { transform: translateY(100%); }
                }
                @keyframes crtFlicker {
                  0% { opacity: 1; }
                  10% { opacity: 0.98; }
                  20% { opacity: 1; }
                  30% { opacity: 0.99; }
                  40% { opacity: 1; }
                  50% { opacity: 0.97; }
                  60% { opacity: 1; }
                  70% { opacity: 0.99; }
                  80% { opacity: 1; }
                  90% { opacity: 0.98; }
                  100% { opacity: 1; }
                }
              `}</style>
            </div>
            </Magnet>
          </div>
      {/* Mouse-like scroll down UI at bottom */}
      <div className="fixed z-50 flex flex-col items-center -translate-x-1/2 pointer-events-none left-1/2 bottom-4" style={{opacity: scrollOpacity, transition: 'opacity 0.4s'}}>
        <div className="flex flex-col items-center scroll-mouse-ui">
          <div className="relative flex items-center justify-center h-12 border-2 border-white rounded-full mouse-shape w-7 bg-slate-900/40 backdrop-blur-sm">
            <div className="mouse-wheel w-1.5 h-3 rounded-full bg-white absolute left-1/2 top-3 -translate-x-1/2 animate-bounce" style={{animationDuration:'1.2s'}}></div>
          </div>
          <span className="mt-2 text-xs tracking-wide text-white/70">Scroll down</span>
        </div>
      </div>
    </section>
  );
};

export default CompanyIntro;